import React, { useState, useMemo, useCallback, memo, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit3, Trash2, RefreshCw, Monitor, Package, ExternalLink, Loader, X, ChevronRight, ChevronLeft, Link2, QrCode, List, Search, ChevronDown, Minus } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { layouts } from '../../../store/useStore';
import { useBannersWrite } from '../../banners/hooks/useBanners';
import { bannerService, TelaBanner, TelaBannerParaScreens, ProdutoBanner, ProdutoBannerOtimizado } from '../../banners/services/bannerService';
// ⚠️ CACHE EXCLUSIVO: bannerCache é usado APENAS nesta página (ScreensPageIntegrated)
// NÃO usar este cache em outras páginas que usam useBanners()
import { bannerCache } from '../../banners/hooks/bannerCache';
import { TIPO_TO_LAYOUT, LAYOUT_TO_TIPO, bannerSyncService, adicionarDescricaoAoJson, extrairDescricaoDoJson, adicionarTagAoJson, extrairTagDoJson, extrairTagsUnicasDasTelas, extrairSequenciasUnicasDasTelas, buscarTelasPorSequencia, extrairDescricaoSequenciaDoJson, adicionarNumeroColunasAoJson, extrairNumeroColunasDoJson, criarConfiguracoesDefaultParaTipo } from '../../banners/services/bannerSyncService';
import { Screen, LayoutType } from '../../../shared/types';
import { ScreenProductsModal } from '../components/ScreenProductsModal';
import { ScreenFilters } from '../components/ScreenFilters';
import { priceService, Categoria } from '../../promotions/services/priceService';
import { useCategories } from '../../promotions/hooks/useCategories';
import { authService } from '../../auth/services/authService';
import { apiService } from '../../../shared/services/apiService';
import { televisaoService, Televisao } from '../../televisao/services/televisaoService';

// Map de layouts para busca O(1) - criado uma vez fora do componente
const layoutsMap = new Map(layouts.map(l => [l.id, l.name]));

// Componente memoizado para o card de tela - evita re-renderizações desnecessárias
const ScreenCard = memo(({
    tela,
    onEdit,
    onDelete,
    onViewProducts,
    onAddProduct,
    onRemoveProduct
}: {
    tela: any;
    onEdit: (tela: any) => void;
    onDelete: (id: number) => void;
    onViewProducts: (id: number, name: string) => void;
    onAddProduct: (id: number, name: string) => void;
    onRemoveProduct: (id: number, name: string) => void;
}) => {
    const tipoNome = useMemo(() => {
        const layout = TIPO_TO_LAYOUT[tela.tipo ?? 0];
        return layoutsMap.get(layout) || 'Desconhecido';
    }, [tela.tipo]);

    // Contagem de produtos - otimizado para evitar recriações
    const produtoCount = useMemo(() => {
        if (!tela.produtoIds) return 0;
        return Array.isArray(tela.produtoIds) ? tela.produtoIds.length : 0;
    }, [tela.produtoIds?.length]); // Usar apenas length como dependência

    // Extrair descrição: primeiro do campo descricao, depois do JSON de configurações
    const descricao = useMemo(() => {
        if (tela.descricao) return tela.descricao;
        if (tela.configuracoes) {
            return extrairDescricaoDoJson(tela.configuracoes) || undefined;
        }
        return undefined;
    }, [tela.descricao, tela.configuracoes]);

    // Extrair categoria (tag) do JSON de configurações
    const categoria = useMemo(() => {
        if (tela.configuracoes) {
            const categoriaDoJson = extrairTagDoJson(tela.configuracoes);
            if (categoriaDoJson) {
                return categoriaDoJson.toUpperCase();
            }
        }
        return undefined;
    }, [tela.configuracoes]);

    return (
        <div
            className={`bg-white rounded-lg shadow-md p-3 sm:p-4 lg:p-6 border-2 h-full flex flex-col ${tela.ativo ? 'border-green-500' : 'border-gray-200'}`}
        >
            {/* Header - título e descrição com altura mínima */}
            <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div className="flex-1 min-w-0 pr-2">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 truncate">{tela.nome}</h3>
                    <p className="text-xs sm:text-sm text-gray-500 line-clamp-2 min-h-[2rem] sm:min-h-[2.5rem]">
                        {descricao || <span className="text-gray-400 italic">Sem descrição</span>}
                    </p>
                </div>
                <div className="flex gap-1 sm:gap-2 flex-shrink-0">
                    <button
                        onClick={() => onEdit(tela)}
                        className="text-blue-500 hover:text-blue-600 p-1"
                    >
                        <Edit3 size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </button>
                    <button
                        onClick={() => tela.id && onDelete(tela.id)}
                        className="text-red-500 hover:text-red-600 p-1"
                    >
                        <Trash2 size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </button>
                </div>
            </div>

            {/* Content - área flexível que preenche o espaço */}
            <div className="space-y-1.5 sm:space-y-2 flex-1">
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <span className="font-bold mr-2">ID:</span>
                    <span className="font-bold">{tela.id ?? '-'}</span>
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600 min-h-[1.25rem] sm:min-h-[1.5rem]">
                    <span className="font-medium mr-2">Categoria:</span>
                    {categoria ? (
                        <span className="font-semibold text-blue-600 uppercase text-xs sm:text-sm">{categoria}</span>
                    ) : (
                        <span className="text-gray-400 italic text-xs sm:text-sm">Sem categoria</span>
                    )}
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600">
                    <span className="font-medium mr-2">Tipo:</span>
                    <span className="text-xs sm:text-sm">{tipoNome}</span>
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600 gap-1.5 sm:gap-2 flex-wrap">
                    {produtoCount > 0 ? (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (tela.id) {
                                    onViewProducts(tela.id, tela.nome);
                                }
                            }}
                            className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-1 rounded-md text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors flex-shrink-0"
                        >
                            <Package size={12} className="sm:w-[14px] sm:h-[14px]" />
                            <span className="font-medium whitespace-nowrap text-xs sm:text-sm">
                                {produtoCount} {produtoCount === 1 ? 'produto' : 'produtos'}
                            </span>
                        </button>
                    ) : (
                        <span className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-1 text-gray-400 flex-shrink-0">
                            <Package size={12} className="sm:w-[14px] sm:h-[14px]" />
                            <span className="font-medium whitespace-nowrap text-xs sm:text-sm">0 produtos</span>
                        </span>
                    )}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            if (tela.id) {
                                onAddProduct(tela.id, tela.nome);
                            }
                        }}
                        className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-1 rounded-md text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors border border-blue-300 flex-shrink-0 text-xs sm:text-sm"
                    >
                        <Plus size={12} className="sm:w-[14px] sm:h-[14px] text-blue-500" />
                        <span className="font-medium">Adicionar</span>
                    </button>
                    {produtoCount > 0 && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                if (tela.id) {
                                    onRemoveProduct(tela.id, tela.nome);
                                }
                            }}
                            className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-1 rounded-md text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors border border-red-300 flex-shrink-0 text-xs sm:text-sm"
                        >
                            <Minus size={12} className="sm:w-[14px] sm:h-[14px] text-red-500" />
                            <span className="font-medium">Remover</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Actions - sempre no final do card */}
            <div className="mt-3 sm:mt-4 pt-2">
                <Link
                    to={`/display/${tela.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg transition-colors bg-green-500 text-white hover:bg-green-600 text-sm sm:text-base"
                >
                    <Monitor size={16} className="sm:w-[18px] sm:h-[18px]" />
                    <span className="whitespace-nowrap">Abrir Visualização</span>
                    <ExternalLink size={14} className="sm:w-[16px] sm:h-[16px]" />
                </Link>
            </div>
        </div>
    );
});

ScreenCard.displayName = 'ScreenCard';

/**
 * ⚠️ PÁGINA SCREENS - USO EXCLUSIVO DO bannerCache ⚠️
 * 
 * Esta página usa bannerCache para manter dados em memória mesmo ao navegar entre menus.
 * 
 * Comportamento:
 * - Carrega telas APENAS uma vez quando a pessoa entra na página
 * - Mantém dados em cache mesmo ao navegar para outras páginas do programa
 * - Recarrega apenas se: reiniciar a página OU clicar no botão sincronizar
 * 
 * IMPORTANTE: bannerCache é EXCLUSIVO desta página. Outras páginas devem usar useBanners() normalmente.
 */
const ScreensPageIntegrated = () => {
    // Estado local sincronizado com o cache EXCLUSIVO desta página
    // Usa TelaBannerParaScreens para listagem otimizada, mas mantém compatibilidade com TelaBanner
    const [telasAPI, setTelasAPI] = useState<TelaBanner[]>(bannerCache.getTelas());
    const [loadingAPI, setLoadingAPI] = useState(false);
    const [errorAPI, setErrorAPI] = useState<string | null>(bannerCache.getError());

    // Hook apenas para operações de escrita
    const {
        criarTela: criarTelaAPI,
        atualizarTela: atualizarTelaAPI,
        deletarTela: deletarTelaAPI
    } = useBannersWrite();

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [selectedScreenForProducts, setSelectedScreenForProducts] = useState<{ id: number; name: string } | null>(null);
    const [selectedScreenForAddProduct, setSelectedScreenForAddProduct] = useState<{ id: number; name: string } | null>(null);
    const [selectedScreenForRemoveProduct, setSelectedScreenForRemoveProduct] = useState<{ id: number; name: string } | null>(null);
    const [showLinkScreenModal, setShowLinkScreenModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Estados para filtros locais
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedType, setSelectedType] = useState<'all' | number>('all');
    const [descriptionFilter, setDescriptionFilter] = useState<'all' | 'with' | 'without'>('all');
    const [productsFilter, setProductsFilter] = useState<'all' | 'with' | 'without'>('all');
    const [selectedCategory, setSelectedCategory] = useState<'all' | string>('all');

    const [formData, setFormData] = useState({
        nome: '',
        descricao: '',
        categoria: '',
        tipo: 0,
        ativo: false,
        numeroColunas: 2 // Número de colunas (1, 2 ou 3) - apenas para tipo 0
    });

    // Estado para controlar criação de nova categoria
    const [showCreateCategory, setShowCreateCategory] = useState(false);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');

    // Extrair todas as tags únicas das telas para o dropdown
    const categoriasDisponiveis = useMemo(() => {
        return extrairTagsUnicasDasTelas(telasAPI);
    }, [telasAPI]);

    // Filtrar categorias baseado no termo de pesquisa
    const categoriasFiltradas = useMemo(() => {
        if (!categorySearchTerm.trim()) {
            return categoriasDisponiveis;
        }
        const searchLower = categorySearchTerm.toLowerCase();
        return categoriasDisponiveis.filter(cat =>
            cat.toLowerCase().includes(searchLower)
        );
    }, [categoriasDisponiveis, categorySearchTerm]);

    // Função para carregar telas da API
    const carregarTelas = useCallback(async () => {
        // Se já está carregando, não faz nada
        if (bannerCache.isLoadingState()) {
            return;
        }

        setLoadingAPI(true);
        bannerCache.setLoading(true);
        setErrorAPI(null);
        bannerCache.setError(null);

        try {
            // Usar método otimizado para página de Screens
            // Retorna apenas: id, nome, tipo, ativo, descricao, categoria (tag), produtoIds
            const result = await bannerService.listarTelasOtimizadoParaScreens();
            const currentCompany = apiService.getCompany();
            // Converter para TelaBanner para manter compatibilidade com o cache
            const telasConvertidas: TelaBanner[] = result.map(tela => ({
                id: tela.id,
                nome: tela.nome,
                tipo: tela.tipo,
                ativo: tela.ativo,
                descricao: tela.descricao,
                // Converter produtoIds (number[]) para ProdutoBanner[] (apenas com produtoId)
                produtoIds: tela.produtoIds?.map((produtoId): ProdutoBanner => ({ produtoId })) || [],
                // Criar configuracoes JSON mínimo com descricao e tag se existirem
                configuracoes: (tela.descricao || tela.categoria) 
                    ? JSON.stringify({
                        ...(tela.descricao ? { descricao: tela.descricao } : {}),
                        ...(tela.categoria ? { tag: tela.categoria } : {})
                    })
                    : undefined
            }));
            bannerCache.setTelas(telasConvertidas, currentCompany);
            setTelasAPI(telasConvertidas);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar telas';
            setErrorAPI(errorMessage);
            bannerCache.setError(errorMessage);
        } finally {
            setLoadingAPI(false);
            bannerCache.setLoading(false);
        }
    }, []);

    // Carregar APENAS uma vez quando a página monta pela primeira vez
    // Se já tem dados no cache, usa eles. Senão, carrega da API.
    useEffect(() => {
        const currentCompany = apiService.getCompany(); // Identificar empresa atual
        if (bannerCache.hasData(currentCompany)) {
            // Já tem dados no cache da MESMA empresa, apenas sincroniza o estado
            const cachedTelas = bannerCache.getTelas();
            setTelasAPI(cachedTelas);
        } else {
            // Não tem dados no cache ou é de outra empresa, carrega da API
            carregarTelas();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Array vazio = executa apenas uma vez ao montar

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        setSaving(true);
        setSaveMessage(null);

        try {
            if (editingId) {
                // IMPORTANTE: Buscar a tela específica da API antes de salvar para pegar configurações mais recentes
                // Isso garante que se o usuário alterou o estilo em outra página e voltou sem sincronizar,
                // vamos pegar as configurações atualizadas da API ao invés do cache desatualizado
                const telasAtualizadasAPI = await bannerService.listarTelasOtimizado(editingId);
                const telaAtualizadaAPI = telasAtualizadasAPI.find(t => t.id === editingId);

                if (!telaAtualizadaAPI) {
                    throw new Error('Tela não encontrada na API');
                }

                const tipoAnterior = telaAtualizadaAPI.tipo ?? 0;
                const tipoNovo = formData.tipo;
                const tipoMudou = tipoAnterior !== tipoNovo;
                const layoutNovo = (TIPO_TO_LAYOUT[tipoNovo] || 'simple2col') as LayoutType;

                let configuracoesAtualizadas: string | undefined;

                if (tipoMudou) {
                    // Novo tipo: substituir configurações pelo padrão do layout (evita misturar colunas / panfleto / vídeo)
                    configuracoesAtualizadas = criarConfiguracoesDefaultParaTipo(layoutNovo);
                    configuracoesAtualizadas = adicionarDescricaoAoJson(
                        configuracoesAtualizadas,
                        formData.descricao
                    );
                    const categoriaUpperMudanca = formData.categoria ? formData.categoria.toUpperCase().trim() : '';
                    configuracoesAtualizadas = adicionarTagAoJson(configuracoesAtualizadas, categoriaUpperMudanca);
                    if (formData.tipo === 0) {
                        configuracoesAtualizadas = adicionarNumeroColunasAoJson(
                            configuracoesAtualizadas,
                            formData.numeroColunas
                        );
                    }
                } else {
                    // IMPORTANTE: Adicionar descrição ao JSON de configurações
                    configuracoesAtualizadas = adicionarDescricaoAoJson(
                        telaAtualizadaAPI.configuracoes,
                        formData.descricao
                    );

                    // IMPORTANTE: Adicionar tag (categoria) ao JSON de configurações
                    const categoriaUpper = formData.categoria ? formData.categoria.toUpperCase().trim() : '';
                    configuracoesAtualizadas = adicionarTagAoJson(
                        configuracoesAtualizadas,
                        categoriaUpper
                    );

                    // IMPORTANTE: Adicionar número de colunas ao JSON de configurações (apenas para tipo 0)
                    if (formData.tipo === 0) {
                        configuracoesAtualizadas = adicionarNumeroColunasAoJson(
                            configuracoesAtualizadas,
                            formData.numeroColunas
                        );
                    }
                }

                // IMPORTANTE: Construir comando SEM produtoIds
                // A API ignora produtoIds quando não enviado, e não precisamos mais enviá-los na edição de tela
                const command: any = {
                    id: editingId,
                    nome: formData.nome,
                    tipo: formData.tipo,
                    configuracoes: configuracoesAtualizadas // Configurações atualizadas da API com descrição e tag
                    // NÃO incluir produtoIds - a API ignora quando não enviado
                };

                // Enviar atualização para a API
                const sucesso = await bannerService.atualizarTela(command);

                if (!sucesso) {
                    throw new Error('Erro ao atualizar tela na API');
                }

                // OTIMIZAÇÃO: Atualizar cache com os dados atualizados da API
                // IMPORTANTE: Manter produtoIds da API para exibição, mas não enviar no PUT
                const telasAtualizadas = telasAPI.map(tela =>
                    tela.id === editingId
                        ? {
                            ...tela,
                            nome: formData.nome,
                            descricao: formData.descricao, // Atualizar descrição
                            tipo: formData.tipo,
                            // Manter produtoIds da API para exibição (não alteramos eles no PUT)
                            produtoIds: telaAtualizadaAPI.produtoIds as any,
                            configuracoes: configuracoesAtualizadas // Atualizar configurações com descrição e tag
                        }
                        : tela
                );
                bannerCache.setTelas(telasAtualizadas);
                setTelasAPI(telasAtualizadas);

                setSaveMessage({ type: 'success', text: 'Tela atualizada com sucesso!' });
            } else {
                // Criar nova tela na API
                const resultado = await criarTelaAPI({
                    nome: formData.nome,
                    tipo: formData.tipo
                    // NÃO incluir descricao - não existe na API
                    // NÃO incluir ativo - não existe na API
                });

                // A API pode retornar 'telaId' ou 'id' - usar qualquer um que estiver disponível
                const telaId = (resultado as any).telaId ?? resultado.id;

                // Verificar se o ID foi retornado (aceita 0 como valor válido)
                if (telaId === undefined || telaId === null) {
                    throw new Error('ID da tela não foi retornado pela API');
                }

                // Configurações iniciais: panfleto (tipo 1) sempre recebe JSON completo do layout; demais seguem lógica mínima
                let configuracoesIniciais: string | undefined = undefined;
                if (formData.tipo === 1) {
                    configuracoesIniciais = criarConfiguracoesDefaultParaTipo('panfleto');
                    if (formData.descricao?.trim()) {
                        configuracoesIniciais = adicionarDescricaoAoJson(configuracoesIniciais, formData.descricao.trim()) ?? configuracoesIniciais;
                    }
                    if (formData.categoria?.trim()) {
                        const categoriaUpper = formData.categoria.toUpperCase().trim();
                        configuracoesIniciais = adicionarTagAoJson(configuracoesIniciais, categoriaUpper) ?? configuracoesIniciais;
                    }
                } else if (formData.descricao || formData.categoria || (formData.tipo === 0 && formData.numeroColunas !== 2)) {
                    let config: string | undefined = '{}';
                    if (formData.descricao) {
                        config = adicionarDescricaoAoJson(config, formData.descricao) || config;
                    }
                    if (formData.categoria && config) {
                        const categoriaUpper = formData.categoria.toUpperCase().trim();
                        config = adicionarTagAoJson(config, categoriaUpper) || config;
                    }
                    if (formData.tipo === 0 && config) {
                        config = adicionarNumeroColunasAoJson(config, formData.numeroColunas) || config;
                    }
                    configuracoesIniciais = config;
                }

                // OTIMIZAÇÃO: Adicionar nova tela no cache diretamente ao invés de fazer nova busca
                const novaTela: TelaBanner = {
                    id: telaId, // Usar telaId se disponível, caso contrário usar id
                    nome: resultado.nome || formData.nome || 'Tela sem nome', // Fallback para o nome do formulário caso a API não retorne
                    tipo: formData.tipo,
                    produtoIds: [] as any, // Nova tela começa sem produtos (array vazio)
                    configuracoes: configuracoesIniciais // Nova tela começa com configurações se houver descrição ou categoria
                };

                // Se houver configurações (incl. panfleto novo com JSON padrão), persistir na API
                // NÃO enviar produtoIds - a API ignora quando não enviado
                if (configuracoesIniciais) {
                    const command: any = {
                        id: telaId,
                        nome: novaTela.nome,
                        tipo: novaTela.tipo,
                        configuracoes: configuracoesIniciais
                        // NÃO incluir produtoIds - a API ignora quando não enviado
                    };
                    await bannerService.atualizarTela(command);
                }

                // Atualizar estado de forma imediata para garantir que o ID apareça
                setTelasAPI(prevTelas => {
                    const telasAtualizadas = [...prevTelas, novaTela];
                    bannerCache.setTelas(telasAtualizadas);
                    return telasAtualizadas;
                });

                setSaveMessage({ type: 'success', text: 'Tela criada com sucesso!' });
            }

            // Limpar mensagem após 2 segundos e fechar o modal
            setTimeout(() => {
                setSaveMessage(null);
                setShowForm(false);
                setEditingId(null);
                setFormData({ nome: '', descricao: '', categoria: '', tipo: 0, ativo: false, numeroColunas: 2 });
                setShowCreateCategory(false);
                setShowCategoryDropdown(false);
                setCategorySearchTerm('');
            }, 2000);
        } catch (error) {
            setSaveMessage({
                type: 'error',
                text: `Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`
            });
        } finally {
            setSaving(false);
        }
    }, [editingId, formData, criarTelaAPI, telasAPI]);

    const handleEdit = useCallback((tela: any) => {
        // IMPORTANTE: Extrair descrição do JSON de configurações se não estiver no campo descricao
        let descricao = tela.descricao || '';
        if (!descricao && tela.configuracoes) {
            const descricaoDoJson = extrairDescricaoDoJson(tela.configuracoes);
            if (descricaoDoJson) {
                descricao = descricaoDoJson;
            }
        }

        // IMPORTANTE: Extrair categoria (tag) do JSON de configurações
        let categoria = '';
        if (tela.configuracoes) {
            const categoriaDoJson = extrairTagDoJson(tela.configuracoes);
            if (categoriaDoJson) {
                categoria = categoriaDoJson;
            }
        }

        // IMPORTANTE: Extrair número de colunas do JSON de configurações (apenas para tipo 0)
        let numeroColunas = 2; // Padrão: 2 colunas
        if (tela.configuracoes && (tela.tipo ?? 0) === 0) {
            const numeroColunasDoJson = extrairNumeroColunasDoJson(tela.configuracoes);
            if (numeroColunasDoJson !== undefined && numeroColunasDoJson >= 1 && numeroColunasDoJson <= 3) {
                numeroColunas = numeroColunasDoJson;
            }
        }

        setEditingId(tela.id);
        setFormData({
            nome: tela.nome,
            descricao: descricao,
            categoria: categoria,
            tipo: tela.tipo ?? 0,
            ativo: tela.ativo ?? false,
            numeroColunas: numeroColunas
        });
        setShowForm(true);
    }, []);

    const handleDelete = useCallback(async (id: number) => {
        if (!window.confirm('Excluir esta tela? Ela será removida automaticamente de todas as televisões onde está cadastrada.')) return;

        try {
            // Primeiro, remover a tela de todas as televisões onde ela está cadastrada
            console.log(`🗑️ [ScreensPage] Removendo tela ${id} de todas as televisões...`);
            try {
                const televisoesAtualizadas = await televisaoService.removerTelaDeTodasTelevisoes(id);
                if (televisoesAtualizadas > 0) {
                    console.log(`✅ [ScreensPage] Tela ${id} removida de ${televisoesAtualizadas} televisão(ões)`);
                }
            } catch (error) {
                console.error(`⚠️ [ScreensPage] Erro ao remover tela das televisões (continuando com a deleção):`, error);
                // Continuar com a deleção mesmo se houver erro ao remover das televisões
            }

            // Depois, deletar a tela (panfleto: foto em tb_banner_tela_fotos antes da tela)
            const tela = telasAPI.find(t => t.id === id);
            await deletarTelaAPI(id, tela?.tipo);
            await carregarTelas();
        } catch (error) {
            alert(`Erro: ${error}`);
        }
    }, [deletarTelaAPI, carregarTelas, telasAPI]);

    const handleViewProducts = useCallback((id: number, name: string) => {
        setSelectedScreenForProducts({ id, name });
    }, []);

    const handleAddProduct = useCallback((id: number, name: string) => {
        setSelectedScreenForAddProduct({ id, name });
    }, []);

    const handleRemoveProduct = useCallback((id: number, name: string) => {
        setSelectedScreenForRemoveProduct({ id, name });
    }, []);

    // Atualizar produtos da tela no cache local sem recarregar da API
    const handleUpdateScreenProducts = useCallback((screenId: number, newProductIds: number[]) => {
        setTelasAPI(prevTelas => {
            const novasTelas = prevTelas.map(t => {
                if (t.id === screenId) {
                    return { ...t, produtoIds: newProductIds as any };
                }
                return t;
            });
            // Atualizar também o bannerCache para persistir na sessão
            bannerCache.setTelas(novasTelas);
            return novasTelas;
        });
    }, []);

    const handleRefresh = useCallback(() => {
        // Limpar cache e recarregar
        bannerCache.clear();
        carregarTelas();
    }, [carregarTelas]);

    // Filtrar telas localmente baseado nos filtros
    const filteredTelas = useMemo(() => {
        return telasAPI.filter(tela => {
            // Filtro por nome
            const matchesName = !searchTerm.trim() ||
                (tela.nome && tela.nome.toLowerCase().includes(searchTerm.toLowerCase()));

            // Filtro por tipo
            const matchesType = selectedType === 'all' || tela.tipo === selectedType;

            // Filtro por descrição
            // Extrair descrição: primeiro do campo descricao, depois do JSON de configurações
            const descricao = tela.descricao || (tela.configuracoes ? extrairDescricaoDoJson(tela.configuracoes) : null);
            const hasDescription = !!descricao && descricao.trim().length > 0;

            const matchesDescription =
                descriptionFilter === 'all' ||
                (descriptionFilter === 'with' && hasDescription) ||
                (descriptionFilter === 'without' && !hasDescription);

            // Filtro por produtos
            const produtoIds = tela.produtoIds;
            const hasProducts = Array.isArray(produtoIds) && produtoIds.length > 0;

            const matchesProducts =
                productsFilter === 'all' ||
                (productsFilter === 'with' && hasProducts) ||
                (productsFilter === 'without' && !hasProducts);

            // Filtro por categoria
            const categoria = tela.configuracoes ? extrairTagDoJson(tela.configuracoes) : null;
            const categoriaUpper = categoria ? categoria.toUpperCase() : null;

            const matchesCategory =
                selectedCategory === 'all' ||
                (selectedCategory !== 'all' && categoriaUpper === selectedCategory.toUpperCase());

            return matchesName && matchesType && matchesDescription && matchesProducts && matchesCategory;
        });
    }, [telasAPI, searchTerm, selectedType, descriptionFilter, productsFilter, selectedCategory]);

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Telas</h1>
                    <p className="text-gray-600 mt-1 text-sm sm:text-base">Gerencie as telas de exibição</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    <button
                        onClick={handleRefresh}
                        disabled={loadingAPI}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 text-sm sm:text-base"
                    >
                        <RefreshCw size={18} className={`sm:w-[20px] sm:h-[20px] ${loadingAPI ? 'animate-spin' : ''}`} />
                        <span className="whitespace-nowrap">Sincronizar</span>
                    </button>
                    <button
                        onClick={() => setShowLinkScreenModal(true)}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm sm:text-base"
                    >
                        <Link2 size={18} className="sm:w-[20px] sm:h-[20px]" />
                        <span className="whitespace-nowrap">Vincular ao Totem</span>
                    </button>
                    <button
                        onClick={() => {
                            setEditingId(null);
                            setFormData({ nome: '', descricao: '', categoria: '', tipo: 0, ativo: false, numeroColunas: 2 });
                            setShowCreateCategory(false);
                            setShowCategoryDropdown(false);
                            setCategorySearchTerm('');
                            setShowForm(true);
                        }}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm sm:text-base"
                    >
                        <Plus size={18} className="sm:w-[20px] sm:h-[20px]" />
                        <span className="whitespace-nowrap">Nova Tela</span>
                    </button>
                </div>
            </div>

            {errorAPI && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-700">Erro: {errorAPI}</p>
                </div>
            )}

            {/* Filtros */}
            <ScreenFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectedType={selectedType}
                onTypeChange={setSelectedType}
                descriptionFilter={descriptionFilter}
                onDescriptionFilterChange={setDescriptionFilter}
                productsFilter={productsFilter}
                onProductsFilterChange={setProductsFilter}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                categoriasDisponiveis={categoriasDisponiveis}
                loading={loadingAPI}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {loadingAPI ? (
                    <div className="col-span-full py-12 text-center">
                        <div className="flex flex-col items-center justify-center text-gray-500">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-4"></div>
                            <p className="text-lg font-medium">Carregando telas...</p>
                        </div>
                    </div>
                ) : filteredTelas && filteredTelas.length > 0 ? (
                    filteredTelas.map((tela, index) => (
                        <ScreenCard
                            key={tela.id ?? `tela-${index}`}
                            tela={tela}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onViewProducts={handleViewProducts}
                            onAddProduct={handleAddProduct}
                            onRemoveProduct={handleRemoveProduct}
                        />
                    ))
                ) : (
                    <div className="col-span-full text-center py-12">
                        <p className="text-gray-500 text-lg">
                            Nenhuma tela encontrada com os filtros aplicados.
                        </p>
                    </div>
                )}
            </div>

            {showForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="p-6 overflow-y-auto flex-1">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">
                                {editingId ? 'Editar Tela' : 'Nova Tela'}
                            </h2>

                        {/* Save Message */}
                        {saveMessage && (
                            <div className={`mb-4 p-4 rounded-lg ${saveMessage.type === 'success'
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-red-50 text-red-700 border border-red-200'
                                }`}>
                                <p className="font-medium">{saveMessage.text}</p>
                            </div>
                        )}

                        {/* Saving Indicator */}
                        {saving && !saveMessage && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <div className="flex items-center gap-2">
                                    <Loader size={18} className="animate-spin text-blue-500" />
                                    <p className="text-blue-700 font-medium">Salvando...</p>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Nome *
                                </label>
                                <input
                                    type="text"
                                    value={formData.nome}
                                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Descrição
                                </label>
                                <textarea
                                    value={formData.descricao}
                                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                    rows={3}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Categoria
                                </label>
                                {showCreateCategory ? (
                                    <input
                                        type="text"
                                        value={formData.categoria}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value.length <= 50) {
                                                setFormData({ ...formData, categoria: value });
                                            }
                                        }}
                                        placeholder="Digite o nome da categoria (máx. 50 caracteres)"
                                        className="w-full p-3 border border-gray-300 rounded-lg"
                                        maxLength={50}
                                        autoFocus
                                    />
                                ) : (
                                    <div className="relative">
                                        <button
                                            type="button"
                                            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                                            className="w-full p-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between hover:border-gray-400 transition-colors"
                                        >
                                            <span className={formData.categoria ? 'text-gray-900' : 'text-gray-500'}>
                                                {formData.categoria || 'Selecione uma categoria'}
                                            </span>
                                            <svg
                                                className={`w-5 h-5 text-gray-400 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {showCategoryDropdown && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden flex flex-col">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setShowCreateCategory(true);
                                                        setShowCategoryDropdown(false);
                                                        setFormData({ ...formData, categoria: '' });
                                                        setCategorySearchTerm('');
                                                    }}
                                                    className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-center gap-2 text-blue-600 font-medium border-b border-gray-200"
                                                >
                                                    <Plus size={18} className="text-blue-500" />
                                                    Criar Categoria
                                                </button>
                                                <div className="border-b border-gray-200 p-2">
                                                    <input
                                                        type="text"
                                                        value={categorySearchTerm}
                                                        onChange={(e) => setCategorySearchTerm(e.target.value)}
                                                        placeholder="Pesquisar categoria..."
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="overflow-y-auto max-h-48">
                                                    {categoriasFiltradas.length > 0 ? (
                                                        categoriasFiltradas.map((cat) => (
                                                            <button
                                                                key={cat}
                                                                type="button"
                                                                onClick={() => {
                                                                    setFormData({ ...formData, categoria: cat.toUpperCase() });
                                                                    setShowCategoryDropdown(false);
                                                                    setCategorySearchTerm('');
                                                                }}
                                                                className="w-full px-4 py-2.5 text-left hover:bg-gray-50 text-gray-900 uppercase"
                                                            >
                                                                {cat}
                                                            </button>
                                                        ))
                                                    ) : (
                                                        <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                            {categorySearchTerm.trim() ? 'Nenhuma categoria encontrada' : 'Nenhuma categoria disponível'}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {showCategoryDropdown && (
                                            <div
                                                className="fixed inset-0 z-0"
                                                onClick={() => setShowCategoryDropdown(false)}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Tipo *
                                </label>
                                <select
                                    value={formData.tipo}
                                    onChange={(e) => setFormData({ ...formData, tipo: parseInt(e.target.value) })}
                                    className="w-full p-3 border border-gray-300 rounded-lg"
                                >
                                    <option value={0}>Lista com Colunas</option>
                                    <option value={1}>Panfleto</option>
                                    <option value={2}>Vídeo</option>
                                </select>
                            </div>

                            {/* Campo de número de colunas - apenas para tipo 0 (Lista com Colunas) */}
                            {formData.tipo === 0 && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Número de Colunas
                                    </label>
                                    <select
                                        value={formData.numeroColunas}
                                        onChange={(e) => setFormData({ ...formData, numeroColunas: parseInt(e.target.value) })}
                                        className="w-full p-3 border border-gray-300 rounded-lg"
                                    >
                                        <option value={1}>1 Coluna</option>
                                        <option value={2}>2 Colunas</option>
                                        <option value={3}>3 Colunas</option>
                                    </select>
                                </div>
                            )}

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingId(null);
                                        setShowCategoryDropdown(false);
                                        setShowCreateCategory(false);
                                        setCategorySearchTerm('');
                                    }}
                                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saving ? (
                                        <>
                                            <Loader size={18} className="animate-spin" />
                                            {editingId ? 'Atualizando...' : 'Criando...'}
                                        </>
                                    ) : (
                                        editingId ? 'Atualizar' : 'Criar'
                                    )}
                                </button>
                            </div>
                        </form>
                        </div>
                    </div>
                </div>
            )}

            {selectedScreenForProducts && (
                <ScreenProductsModal
                    screenId={selectedScreenForProducts.id}
                    screenName={selectedScreenForProducts.name}
                    promotions={[]}
                    onClose={() => setSelectedScreenForProducts(null)}
                />
            )}

            {selectedScreenForAddProduct && (
                <AddProductModal
                    screenId={selectedScreenForAddProduct.id}
                    screenName={selectedScreenForAddProduct.name}
                    onClose={() => setSelectedScreenForAddProduct(null)}
                    onProductLinked={handleUpdateScreenProducts}
                />
            )}

            {selectedScreenForRemoveProduct && (
                <RemoveProductModal
                    screenId={selectedScreenForRemoveProduct.id}
                    screenName={selectedScreenForRemoveProduct.name}
                    onClose={() => setSelectedScreenForRemoveProduct(null)}
                    onProductUnlinked={handleUpdateScreenProducts}
                />
            )}

            {showLinkScreenModal && (
                <LinkScreenModal
                    telas={telasAPI}
                    onClose={() => setShowLinkScreenModal(false)}
                />
            )}
        </div>
    );
};

// Modal para adicionar produto à tela
const AddProductModal: React.FC<{
    screenId: number;
    screenName: string;
    onClose: () => void;
    onProductLinked: (screenId: number, productIds: number[]) => void;
}> = ({ screenId, screenName, onClose, onProductLinked }) => {
    const [searchType, setSearchType] = useState<'internal' | 'barcode' | 'name'>('internal');
    const [codeValue, setCodeValue] = useState('');
    const [nameValue, setNameValue] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<'all' | number>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    const [searching, setSearching] = useState(false);
    const [products, setProducts] = useState<any[]>([]);
    const [linkedProductIds, setLinkedProductIds] = useState<Set<number>>(new Set());
    const [linking, setLinking] = useState(false);
    const [linkingProductId, setLinkingProductId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Ref para o campo de input para foco automático
    const inputRef = useRef<HTMLInputElement>(null);

    // Focar no input quando o modal abrir, quando o tipo de busca mudar ou quando selecionar uma categoria
    useEffect(() => {
        // Pequeno timeout para garantir que o elemento foi renderizado
        const timer = setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [searchType, selectedCategory]);

    // Cache de sessão para resultados de busca
    const searchCacheRef = useRef<Map<string, { items: any[]; hasNext: boolean }>>(new Map());

    // Buscar categorias
    const { categorias } = useCategories();

    // Estado para dropdown de pesquisa de categoria
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [categorySearchTerm, setCategorySearchTerm] = useState('');

    const filteredCategories = useMemo(() => {
        if (!categorySearchTerm.trim()) {
            return categorias;
        }
        const searchLower = categorySearchTerm.toLowerCase();
        return categorias.filter(cat =>
            cat.secao.toLowerCase().includes(searchLower)
        );
    }, [categorias, categorySearchTerm]);

    // Buscar produto na API
    const handleSearch = useCallback(async (page: number = 1, useCache: boolean = true) => {
        // Validar se há valor para buscar
        if (searchType === 'name') {
            // Para busca por nome, permitir buscar apenas com categoria ou apenas com nome ou ambos
            if (!nameValue.trim() && selectedCategory === 'all') {
                setError('Digite um nome ou selecione uma categoria para buscar');
                return;
            }
        } else {
            if (!codeValue.trim()) {
                setError('Digite um código para buscar');
                return;
            }
        }

        // Para busca por nome, criar chave de cache
        let cacheKey = '';
        if (searchType === 'name') {
            const nomeBusca = nameValue.trim() || '_empty_';
            cacheKey = `name_${nomeBusca}_cat_${selectedCategory}_page_${page}`;

            // Verificar cache
            if (useCache && searchCacheRef.current.has(cacheKey)) {
                const cached = searchCacheRef.current.get(cacheKey)!;
                setProducts(cached.items);
                setHasNextPage(cached.hasNext);
                setCurrentPage(page);
                // Buscar produtos vinculados à tela
                const telas = await bannerService.listarTelasOtimizado(screenId);
                const tela = telas.find(t => t.id === screenId);
                const produtoIdsVinculados = tela?.produtoIds && Array.isArray(tela.produtoIds)
                    ? new Set(tela.produtoIds)
                    : new Set<number>();
                setLinkedProductIds(produtoIdsVinculados);
                return;
            }
        }

        setSearching(true);
        setError(null);

        // Limpar produtos apenas se for nova busca (não paginação)
        if (page === 1) {
            setProducts([]);
            setHasNextPage(false);
            setHasPreviousPage(false);
        }

        try {
            const params: any = {};
            if (searchType === 'internal') {
                const produtoId = parseInt(codeValue.trim(), 10);
                if (isNaN(produtoId) || produtoId <= 0) {
                    setError('Código interno do produto não é válido');
                    setSearching(false);
                    return;
                }
                params.produtoId = produtoId;
            } else if (searchType === 'barcode') {
                params.codigoBarras = codeValue.trim();
            } else {
                // Pesquisa por nome com paginação e categoria
                // Só adicionar pesquisa se houver nome digitado
                if (nameValue.trim()) {
                    params.pesquisa = nameValue.trim();
                }
                params.page = page;
                params.pageSize = 50;

                if (selectedCategory !== 'all') {
                    params.secaoId = selectedCategory;
                }
            }

            const result = await priceService.listarPrecos(params);

            if (result.items && result.items.length > 0) {
                // Buscar produtos vinculados à tela
                const telas = await bannerService.listarTelasOtimizado(screenId);
                const tela = telas.find(t => t.id === screenId);
                const produtoIdsVinculados = tela?.produtoIds && Array.isArray(tela.produtoIds)
                    ? new Set(tela.produtoIds)
                    : new Set<number>();

                setLinkedProductIds(produtoIdsVinculados);

                // Para busca por nome, salvar no cache
                if (searchType === 'name' && cacheKey) {
                    searchCacheRef.current.set(cacheKey, {
                        items: result.items,
                        hasNext: result.hasNext || false
                    });
                }

                // PRIMEIRO: Mostrar o produto encontrado na tela
                setProducts(result.items);
                setHasNextPage(result.hasNext || false);
                setHasPreviousPage(page > 1);
                setCurrentPage(page);

                // VINCULAÇÃO AUTOMÁTICA: Para busca por código interno ou código de barras
                // Se encontrar exatamente um produto e ele não estiver vinculado, vincular automaticamente imediatamente
                if ((searchType === 'internal' || searchType === 'barcode') && result.items.length === 1) {
                    const produtoEncontrado = result.items[0];
                    const produtoId = produtoEncontrado.produtoId;

                    if (produtoId && !produtoIdsVinculados.has(produtoId)) {
                        // Produto encontrado e não está vinculado - vincular automaticamente imediatamente
                        (async () => {
                            try {
                                // Iniciar estado de vinculação (mostra "Vinculando..." no botão)
                                setLinking(true);
                                setLinkingProductId(produtoId);

                                // Obter produtos atuais da tela
                                const telasAtualizadas = await bannerService.listarTelasOtimizado(screenId);
                                const telaAtualizada = telasAtualizadas.find(t => t.id === screenId);
                                const produtosAtuais = telaAtualizada?.produtoIds && Array.isArray(telaAtualizada.produtoIds)
                                    ? [...telaAtualizada.produtoIds]
                                    : [];

                                if (produtosAtuais.includes(produtoId)) {
                                    setLinking(false);
                                    setLinkingProductId(null);
                                    return;
                                }

                                // API faz merge/append: enviar SÓ o ID novo (não a lista completa)
                                const command: any = {
                                    id: screenId,
                                    nome: telaAtualizada?.nome || '',
                                    tipo: telaAtualizada?.tipo ?? 0,
                                    produtoIds: [produtoId]
                                };

                                // Executar a vinculação e aguardar um tempo mínimo para mostrar o estado "Vinculando..."
                                const [sucesso] = await Promise.all([
                                    bannerService.atualizarTela(command),
                                    new Promise(resolve => setTimeout(resolve, 900)) // Delay mínimo de 900ms para mostrar o estado
                                ]);

                                if (sucesso) {
                                    // Atualizar lista de produtos vinculados
                                    const novosVinculados = new Set(produtoIdsVinculados);
                                    novosVinculados.add(produtoId);
                                    setLinkedProductIds(novosVinculados);
                                    setError(null);

                                    // Atualizar cache local da tela para refletir na contagem
                                    onProductLinked(screenId, Array.from(novosVinculados));
                                } else {
                                    throw new Error('Erro ao vincular produto');
                                }
                            } catch (linkErr) {
                                // Se der erro na vinculação automática, apenas mostrar o produto
                                // Não bloquear a exibição do resultado
                                console.warn('Erro ao vincular produto automaticamente:', linkErr);
                                setError('Erro ao vincular produto automaticamente. Tente vincular manualmente.');
                            } finally {
                                setLinking(false);
                                setLinkingProductId(null);
                            }
                        })();
                    }
                    // Se já estiver vinculado, não fazer nada (conforme solicitado)
                }
            } else {
                setError('Nenhum produto encontrado');
                setProducts([]);
                setHasNextPage(false);
            }
        } catch (err) {
            // Tratar erros da API de forma amigável
            let errorMessage = 'Erro ao buscar produto';

            if (err instanceof Error) {
                const errorStr = err.message.toLowerCase();
                // Verificar se é erro 400 relacionado a código inválido
                if (errorStr.includes('400') || errorStr.includes('not valid') || errorStr.includes('invalid')) {
                    if (searchType === 'internal') {
                        errorMessage = 'Código interno do produto não é válido';
                    } else if (searchType === 'barcode') {
                        errorMessage = 'Código de barras do produto não é válido';
                    } else {
                        errorMessage = 'Erro ao buscar produto por nome';
                    }
                } else {
                    errorMessage = err.message;
                }
            }

            setError(errorMessage);
            setProducts([]);
            setHasNextPage(false);
            setHasPreviousPage(false);
        } finally {
            setSearching(false);
        }
    }, [searchType, codeValue, nameValue, selectedCategory, screenId]);

    // Navegar para próxima página
    const handleNextPage = useCallback(() => {
        if (hasNextPage && !searching) {
            handleSearch(currentPage + 1, true);
        }
    }, [hasNextPage, currentPage, searching, handleSearch]);

    // Navegar para página anterior
    const handlePreviousPage = useCallback(() => {
        if (hasPreviousPage && !searching && currentPage > 1) {
            handleSearch(currentPage - 1, true);
        }
    }, [hasPreviousPage, currentPage, searching, handleSearch]);

    // Vincular produto à tela
    const handleLink = useCallback(async (produtoParaVincular: any) => {
        if (!produtoParaVincular) return;

        const produtoId = produtoParaVincular.produtoId || produtoParaVincular.id;
        if (!produtoId) return;

        setLinking(true);
        setLinkingProductId(produtoId);
        setError(null);

        try {
            // Verificar se produto já está na tela
            if (linkedProductIds.has(produtoId)) {
                setError('Produto já está vinculado a esta tela');
                setLinking(false);
                setLinkingProductId(null);
                return;
            }

            // Buscar dados da tela atual
            const telas = await bannerService.listarTelasOtimizado(screenId);
            const tela = telas.find(t => t.id === screenId);

            if (!tela) {
                throw new Error('Tela não encontrada');
            }

            // Obter produtos atuais da tela
            const produtosAtuais = tela.produtoIds && Array.isArray(tela.produtoIds)
                ? [...tela.produtoIds]
                : [];

            if (produtosAtuais.includes(produtoId)) {
                setLinking(false);
                setLinkingProductId(null);
                return;
            }

            // API faz merge/append: enviar SÓ o ID novo (não a lista completa)
            const command: any = {
                id: screenId,
                nome: tela.nome,
                tipo: tela.tipo ?? 0,
                produtoIds: [produtoId]
            };

            // Executar a vinculação e aguardar um tempo mínimo para mostrar o estado "Vinculando..."
            const [sucesso] = await Promise.all([
                bannerService.atualizarTela(command),
                new Promise(resolve => setTimeout(resolve, 400)) // Delay mínimo de 900ms para mostrar o estado
            ]);

            if (sucesso) {
                // Atualizar lista de produtos vinculados
                const novosVinculados = new Set(linkedProductIds);
                novosVinculados.add(produtoId);
                setLinkedProductIds(novosVinculados);
                setError(null);

                // Atualizar cache local da tela para refletir na contagem
                onProductLinked(screenId, Array.from(novosVinculados));
            } else {
                throw new Error('Erro ao vincular produto');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao vincular produto');
        } finally {
            setLinking(false);
            setLinkingProductId(null);
        }
    }, [screenId, linkedProductIds]);

    // Função para formatar números no padrão brasileiro
    const formatBrazilianNumber = (value: number | undefined): string => {
        const numValue = value ?? 0;
        return numValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">
                                Adicionar Produto
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Tela: <span className="font-semibold">{screenName}</span>
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-700 text-sm">{error}</p>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Tipo de Busca
                            </label>
                            <select
                                value={searchType}
                                onChange={(e) => {
                                    setSearchType(e.target.value as 'internal' | 'barcode' | 'name');
                                    setCodeValue('');
                                    setNameValue('');
                                    setSelectedCategory('all');
                                    setCurrentPage(1);
                                    setProducts([]);
                                    setError(null);
                                    searchCacheRef.current.clear();
                                }}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                                disabled={searching || linking}
                            >
                                <option value="internal">Código Interno</option>
                                <option value="barcode">Código de Barras</option>
                                <option value="name">Nome do Produto</option>
                            </select>
                        </div>

                        {searchType === 'name' ? (
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Nome do Produto
                                        </label>
                                        <input
                                            ref={inputRef}
                                            type="text"
                                            value={nameValue}
                                            onChange={(e) => {
                                                setNameValue(e.target.value);
                                                setCurrentPage(1);
                                                // Não limpar produtos aqui, apenas quando buscar
                                                setError(null);
                                                searchCacheRef.current.clear();
                                            }}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSearch(1, false);
                                                }
                                            }}
                                            placeholder="Digite o nome do produto"
                                            className="w-full p-3 border border-gray-300 rounded-lg"
                                            disabled={searching || linking}
                                        />
                                    </div>
                                    <div className="relative">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Categoria
                                        </label>

                                        <div className="relative">
                                            <button
                                                type="button"
                                                onClick={() => !searching && !linking && setShowCategoryDropdown(!showCategoryDropdown)}
                                                className={`w-full p-3 border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between transition-colors ${searching || linking ? 'bg-gray-100 cursor-not-allowed opacity-70' : 'hover:border-gray-400'}`}
                                                disabled={searching || linking}
                                            >
                                                <span className="text-gray-900 truncate pr-2">
                                                    {selectedCategory === 'all'
                                                        ? 'Todas as categorias'
                                                        : categorias.find(c => c.secaoId === selectedCategory)?.secao || 'Categoria desconhecida'}
                                                </span>
                                                <ChevronDown size={20} className={`text-gray-400 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                                            </button>

                                            {showCategoryDropdown && (
                                                <>
                                                    <div
                                                        className="fixed inset-0 z-10"
                                                        onClick={() => setShowCategoryDropdown(false)}
                                                    />
                                                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 flex flex-col">
                                                        <div className="p-2 border-b border-gray-200">
                                                            <div className="relative">
                                                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                                <input
                                                                    type="text"
                                                                    value={categorySearchTerm}
                                                                    onChange={(e) => setCategorySearchTerm(e.target.value)}
                                                                    placeholder="Pesquisar categoria..."
                                                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                                    autoFocus
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="overflow-y-auto flex-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setSelectedCategory('all');
                                                                    setCurrentPage(1);
                                                                    searchCacheRef.current.clear();
                                                                    setShowCategoryDropdown(false);
                                                                    setCategorySearchTerm('');
                                                                }}
                                                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${selectedCategory === 'all' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                                            >
                                                                Todas as categorias
                                                                {selectedCategory === 'all' && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                                                            </button>

                                                            {filteredCategories.length > 0 ? (
                                                                filteredCategories.map((cat) => (
                                                                    <button
                                                                        key={cat.secaoId}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setSelectedCategory(cat.secaoId);
                                                                            setCurrentPage(1);
                                                                            searchCacheRef.current.clear();
                                                                            setShowCategoryDropdown(false);
                                                                            setCategorySearchTerm('');
                                                                        }}
                                                                        className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${selectedCategory === cat.secaoId ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}
                                                                    >
                                                                        {cat.secao}
                                                                        {selectedCategory === cat.secaoId && <div className="w-2 h-2 rounded-full bg-blue-500"></div>}
                                                                    </button>
                                                                ))
                                                            ) : (
                                                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                                                    Nenhuma categoria encontrada
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-2 flex justify-end">
                                    <button
                                        onClick={() => handleSearch(1, false)}
                                        disabled={searching || linking || (!nameValue.trim() && selectedCategory === 'all')}
                                        className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {searching ? (
                                            <Loader size={18} className="animate-spin" />
                                        ) : (
                                            'Buscar'
                                        )}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {searchType === 'internal' ? 'Código Interno' : 'Código de Barras'}
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        inputMode="numeric"
                                        value={codeValue}
                                        maxLength={searchType === 'internal' ? 9 : 50}
                                        onChange={(e) => {
                                            // Permitir apenas números
                                            let value = e.target.value.replace(/\D/g, '');
                                            // Aplicar limite de caracteres
                                            const maxLength = searchType === 'internal' ? 9 : 50;
                                            if (value.length > maxLength) {
                                                value = value.substring(0, maxLength);
                                            }
                                            setCodeValue(value);
                                            setProducts([]);
                                            setError(null);
                                        }}
                                        onKeyPress={(e) => {
                                            // Permitir apenas números e Enter
                                            if (e.key === 'Enter') {
                                                handleSearch(1, false);
                                            } else if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                                                e.preventDefault();
                                            }
                                        }}
                                        placeholder={searchType === 'internal' ? 'Digite o código interno (máx. 9 dígitos)' : 'Digite o código de barras (máx. 50 dígitos)'}
                                        className="flex-1 p-3 border border-gray-300 rounded-lg"
                                        disabled={searching || linking}
                                    />
                                    <button
                                        onClick={() => handleSearch(1, false)}
                                        disabled={searching || linking || !codeValue.trim()}
                                        className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        {searching ? (
                                            <Loader size={18} className="animate-spin" />
                                        ) : (
                                            'Buscar'
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Controles de paginação (apenas para busca por nome) */}
                        {searchType === 'name' && products.length > 0 && (hasNextPage || hasPreviousPage) && (
                            <div className="mt-4 flex justify-between items-center">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={searching || !hasPreviousPage || currentPage === 1}
                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {searching ? (
                                        <>
                                            <Loader size={18} className="animate-spin" />
                                            Carregando...
                                        </>
                                    ) : (
                                        <>
                                            <ChevronLeft size={18} />
                                            Página anterior
                                        </>
                                    )}
                                </button>

                                <span className="text-sm text-gray-600">
                                    Página {currentPage}
                                </span>

                                <button
                                    onClick={handleNextPage}
                                    disabled={searching || !hasNextPage}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                >
                                    {searching ? (
                                        <>
                                            <Loader size={18} className="animate-spin" />
                                            Carregando...
                                        </>
                                    ) : (
                                        <>
                                            Próxima página
                                            <ChevronRight size={18} />
                                        </>
                                    )}
                                </button>
                            </div>
                        )}

                        {/* Tabela de produtos - sempre em formato tabela */}
                        {products.length > 0 ? (
                            <div className="mt-4">
                                <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b">
                                                <tr>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-24">ID</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nome</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-32">Preço</th>
                                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-32">Ação</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {products.map((prod) => {
                                                    const produtoId = prod.produtoId || prod.id;
                                                    const jaEstaVinculado = linkedProductIds.has(produtoId);
                                                    const estaVinculando = linkingProductId === produtoId;

                                                    // Preço: usar preço promocional se tiver, senão preço normal
                                                    const preco = prod.vlPreco || prod.vlPrecoNormal || 0;

                                                    return (
                                                        <tr
                                                            key={produtoId}
                                                            className="hover:bg-gray-50 transition-colors"
                                                        >
                                                            {/* ID */}
                                                            <td className="px-4 py-3">
                                                                <span className="text-sm text-gray-600 font-mono font-bold">
                                                                    {produtoId}
                                                                </span>
                                                            </td>

                                                            {/* Nome */}
                                                            <td className="px-4 py-3">
                                                                <span className="text-sm text-gray-900 font-medium">
                                                                    {prod.nome || prod.descricao || 'Produto sem nome'}
                                                                </span>
                                                            </td>

                                                            {/* Preço */}
                                                            <td className="px-4 py-3">
                                                                <span className="text-sm text-gray-900 font-semibold">
                                                                    R$ {formatBrazilianNumber(preco)}
                                                                </span>
                                                            </td>

                                                            {/* Ação */}
                                                            <td className="px-4 py-3">
                                                                {jaEstaVinculado ? (
                                                                    <div className="px-3 py-1 bg-yellow-50 border border-yellow-200 rounded text-center">
                                                                        <p className="text-yellow-800 text-xs font-medium">Vinculado</p>
                                                                    </div>
                                                                ) : (
                                                                    <button
                                                                        onClick={() => handleLink(prod)}
                                                                        disabled={linking && !estaVinculando}
                                                                        className={`px-3 py-1 rounded text-xs font-medium flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${estaVinculando
                                                                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                                                                            : 'bg-green-500 text-white hover:bg-green-600'
                                                                            }`}
                                                                    >
                                                                        {estaVinculando ? (
                                                                            <>
                                                                                <Loader size={14} className="animate-spin" />
                                                                                Vinculando...
                                                                            </>
                                                                        ) : (
                                                                            'Vincular'
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 mt-2">
                                    Total: <span className="font-semibold">{products.length}</span> {products.length === 1 ? 'produto encontrado' : 'produtos encontrados'}
                                </p>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <p className="text-gray-500 text-lg mb-2">Nenhum produto encontrado</p>
                                <p className="text-gray-400 text-sm">Faça uma busca para encontrar produtos</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-6 border-t bg-gray-50">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

// Modal para remover produto da tela
const RemoveProductModal: React.FC<{
    screenId: number;
    screenName: string;
    onClose: () => void;
    onProductUnlinked: (screenId: number, productIds: number[]) => void;
}> = ({ screenId, screenName, onClose, onProductUnlinked }) => {
    const [produtosOtimizados, setProdutosOtimizados] = useState<ProdutoBannerOtimizado[]>([]);
    const [loadingProdutos, setLoadingProdutos] = useState(true);
    const [selectedProductIds, setSelectedProductIds] = useState<Set<number>>(new Set());
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Buscar produtos vinculados à tela
    useEffect(() => {
        const carregarProdutos = async () => {
            setLoadingProdutos(true);
            try {
                const produtos = await bannerService.buscarProdutosTelaOtimizado(screenId);
                setProdutosOtimizados(produtos);
            } catch (error) {
                setError('Erro ao carregar produtos');
                setProdutosOtimizados([]);
            } finally {
                setLoadingProdutos(false);
            }
        };

        carregarProdutos();
    }, [screenId]);

    // Toggle seleção de produto
    const toggleProduct = (productId: number) => {
        setSelectedProductIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                newSet.add(productId);
            }
            return newSet;
        });
    };

    // Remover produtos selecionados
    const handleRemove = useCallback(async () => {
        if (selectedProductIds.size === 0) {
            setError('Selecione pelo menos um produto para remover');
            return;
        }

        setRemoving(true);
        setError(null);

        try {
            const produtoIdsToRemove = Array.from(selectedProductIds).sort((a, b) => a - b);
            const sucesso = await bannerService.desvincularProdutos(screenId, produtoIdsToRemove);

            if (sucesso) {
                // Buscar produtos atualizados da tela
                const telas = await bannerService.listarTelasOtimizado(screenId);
                const tela = telas.find(t => t.id === screenId);
                const produtosAtuais = tela?.produtoIds && Array.isArray(tela.produtoIds)
                    ? [...tela.produtoIds]
                    : [];

                // Atualizar callback
                onProductUnlinked(screenId, produtosAtuais);
                onClose();
            } else {
                setError('Erro ao remover produtos');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao remover produtos');
        } finally {
            setRemoving(false);
        }
    }, [selectedProductIds, screenId, onProductUnlinked, onClose]);

    // Selecionar todos
    const selectAll = () => {
        setSelectedProductIds(new Set(produtosOtimizados.map(p => p.produtoId)));
    };

    // Desmarcar todos
    const deselectAll = () => {
        setSelectedProductIds(new Set());
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-6 border-b">
                    <div className="flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-bold text-gray-800">
                                Remover Produtos
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                Tela: {screenName}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    {loadingProdutos ? (
                        <div className="text-center py-12">
                            <div className="flex flex-col items-center justify-center">
                                <Loader size={32} className="animate-spin text-red-500 mb-4" />
                                <p className="text-gray-600 text-lg font-medium">Carregando produtos...</p>
                            </div>
                        </div>
                    ) : produtosOtimizados.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500 text-lg mb-2">Nenhum produto vinculado</p>
                            <p className="text-gray-400 text-sm">Esta tela não possui produtos para remover</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <p className="text-sm text-gray-600">
                                    Selecione os produtos que deseja remover:
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={selectAll}
                                        className="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded"
                                    >
                                        Selecionar todos
                                    </button>
                                    <button
                                        onClick={deselectAll}
                                        className="text-xs px-2 py-1 text-gray-600 hover:bg-gray-50 rounded"
                                    >
                                        Desmarcar todos
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                                    {error}
                                </div>
                            )}

                            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b">
                                            <tr>
                                                <th className="px-4 py-3 text-left w-12">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedProductIds.size === produtosOtimizados.length && produtosOtimizados.length > 0}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                selectAll();
                                                            } else {
                                                                deselectAll();
                                                            }
                                                        }}
                                                        className="rounded"
                                                    />
                                                </th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-24">ID</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nome</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-32">Preço</th>
                                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 w-24">Unidade</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {produtosOtimizados.map((produto) => (
                                                <tr
                                                    key={produto.produtoId}
                                                    onClick={() => toggleProduct(produto.produtoId)}
                                                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedProductIds.has(produto.produtoId) ? 'bg-red-50' : ''}`}
                                                >
                                                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedProductIds.has(produto.produtoId)}
                                                            onChange={() => toggleProduct(produto.produtoId)}
                                                            className="rounded cursor-pointer"
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm text-gray-600 font-mono font-bold">
                                                            {produto.produtoId}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm text-gray-900 font-medium">{produto.nome || 'Produto sem nome'}</span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm text-gray-900 font-semibold">
                                                            {produto.vlPreco ? `R$ ${produto.vlPreco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <span className="text-sm text-gray-600">{produto.tipoUnidade || 'UN'}</span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <p className="text-sm text-gray-600">
                                {selectedProductIds.size > 0 ? (
                                    <span className="font-semibold text-red-600">
                                        {selectedProductIds.size} {selectedProductIds.size === 1 ? 'produto selecionado' : 'produtos selecionados'}
                                    </span>
                                ) : (
                                    <span>Nenhum produto selecionado</span>
                                )}
                            </p>
                        </div>
                    )}
                </div>

                <div className="p-6 border-t bg-gray-50">
                    <div className="flex justify-between items-center gap-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleRemove}
                            disabled={selectedProductIds.size === 0 || removing}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {removing ? (
                                <>
                                    <Loader size={16} className="animate-spin" />
                                    Removendo...
                                </>
                            ) : (
                                <>
                                    <Minus size={16} />
                                    Remover {selectedProductIds.size > 0 ? `(${selectedProductIds.size})` : ''}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Modal para linkar tela ou televisão
const LinkScreenModal: React.FC<{
    telas: TelaBanner[];
    onClose: () => void;
}> = ({ telas, onClose }) => {
    const navigate = useNavigate();
    const [linkType, setLinkType] = useState<'tela' | 'televisao'>('tela');
    const [inputMode, setInputMode] = useState<'manual' | 'qrcode'>('manual');
    const [showScreenList, setShowScreenList] = useState(false);
    const [selectedScreenId, setSelectedScreenId] = useState<number | null>(null);
    const [selectedTelevisaoId, setSelectedTelevisaoId] = useState<number | null>(null);
    const [screenSearchTerm, setScreenSearchTerm] = useState('');
    const [televisaoSearchTerm, setTelevisaoSearchTerm] = useState('');
    const [qrCodeSearchTerm, setQrCodeSearchTerm] = useState(''); // Pesquisa para modo QR code
    const [showScreenDropdown, setShowScreenDropdown] = useState(false);
    const [showTelevisaoDropdown, setShowTelevisaoDropdown] = useState(false);
    const [televisoes, setTelevisoes] = useState<Televisao[]>([]);
    const [loadingTelevisoes, setLoadingTelevisoes] = useState(false);
    const [showCamera, setShowCamera] = useState(false);
    const [cameraLoading, setCameraLoading] = useState(false);
    const [scannerRunning, setScannerRunning] = useState(false);
    const [codigo, setCodigo] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<boolean>(false);
    const qrCodeScannerRef = useRef<Html5Qrcode | null>(null);
    const cameraContainerRef = useRef<HTMLDivElement>(null);
    const scannerRunningRef = useRef<boolean>(false);

    // Converter telas para formato otimizado
    const telasOtimizadas = useMemo(() => {
        return telas.map(tela => ({
            id: tela.id!,
            nome: tela.nome,
            descricao: tela.descricao,
            tipo: tela.tipo,
            ativo: tela.ativo,
            produtoIds: Array.isArray(tela.produtoIds) ? tela.produtoIds.map((p: any) => typeof p === 'number' ? p : (p.produtoId || p.id || 0)) : [],
            configuracoes: tela.configuracoes
        }));
    }, [telas]);

    // Carregar televisões disponíveis
    useEffect(() => {
        const loadTelevisoes = async () => {
            setLoadingTelevisoes(true);
            try {
                const televisoesList = await televisaoService.listarTelevisoes();
                setTelevisoes(televisoesList.filter(tv => tv.cdTelevisao != null));
            } catch (error) {
                console.error('Erro ao carregar televisões:', error);
                setTelevisoes([]);
            } finally {
                setLoadingTelevisoes(false);
            }
        };
        loadTelevisoes();
    }, []);

    // Função para obter o nome do tipo de tela
    const getTipoTelaNome = useCallback((tipo: number | undefined): string => {
        const tipoNames: Record<number, string> = {
            0: 'Lista com Colunas',
            1: 'Panfleto',
            2: 'Vídeo'
        };
        return tipoNames[tipo ?? 0] || 'Desconhecido';
    }, []);

    // Filtrar telas por pesquisa
    const filteredTelas = useMemo(() => {
        if (!screenSearchTerm.trim()) {
            return telas;
        }
        const searchLower = screenSearchTerm.toLowerCase();
        return telas.filter(tela =>
            tela.nome.toLowerCase().includes(searchLower) ||
            (tela.descricao && tela.descricao.toLowerCase().includes(searchLower))
        );
    }, [telas, screenSearchTerm]);

    // Filtrar televisões por pesquisa
    const filteredTelevisoes = useMemo(() => {
        if (!televisaoSearchTerm.trim()) {
            return televisoes;
        }
        const searchLower = televisaoSearchTerm.toLowerCase();
        return televisoes.filter(tv =>
            tv.nmTelevisao.toLowerCase().includes(searchLower) ||
            (tv.cdTelevisao != null && tv.cdTelevisao.toString().includes(searchLower))
        );
    }, [televisoes, televisaoSearchTerm]);

    // Filtrar telas por pesquisa (modo QR code)
    const filteredTelasQRCode = useMemo(() => {
        if (!qrCodeSearchTerm.trim()) return telas;
        const searchLower = qrCodeSearchTerm.toLowerCase().trim();
        return telas.filter(tela =>
            tela.nome?.toLowerCase().includes(searchLower) ||
            tela.descricao?.toLowerCase().includes(searchLower) ||
            tela.id?.toString().includes(searchLower)
        );
    }, [telas, qrCodeSearchTerm]);

    // Filtrar televisões por pesquisa (modo QR code)
    const filteredTelevisoesQRCode = useMemo(() => {
        if (!qrCodeSearchTerm.trim()) return televisoes;
        const searchLower = qrCodeSearchTerm.toLowerCase().trim();
        return televisoes.filter(tv =>
            tv.nmTelevisao?.toLowerCase().includes(searchLower) ||
            (tv.cdTelevisao != null && tv.cdTelevisao.toString().includes(searchLower))
        );
    }, [televisoes, qrCodeSearchTerm]);

    // Função para fazer o link da tela ou televisão
    const performLink = useCallback(async (codigoTotem: string, identifier: number, isTelevisao: boolean) => {
        if (loading) {
            return;
        }

        // Obter lojaId do token atual
        const lojaId = authService.getLojaId();

        if (!lojaId) {
            setError('Não foi possível obter a loja. Por favor, faça login novamente.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        try {
            if (isTelevisao) {
                // OPERAÇÃO TELEVISÃO: Enviar cdTelevisao no lugar de codigoDisplay
                const cdTelevisao = identifier;
                
                // IMPORTANTE: Enviar cdTelevisao no lugar de codigoDisplay quando for televisão
                await bannerService.validarCodigoTelevisao(codigoTotem.trim(), cdTelevisao, lojaId, undefined, true);

                // NÃO fazer PUT - apenas validar o vínculo
                // O totem vai receber cdTelevisao na resposta e navegar para /display/televisao/:cdTelevisao
            } else {
                // Para telas, usar o ID diretamente
                const codigoTela = identifier;
                const response = await bannerService.validarCodigoTelevisao(codigoTotem.trim(), codigoTela, lojaId);

                try {
                    // IMPORTANTE: Usar código REAL retornado pela validação, não o código falso original
                    const codigoParaUsar = response.codigoReal || codigoTotem.trim();
                    await bannerService.atualizarCodigoDisplay(
                        response.cdMediaPlayerTelevisao,
                        codigoParaUsar, // Usar código REAL, não o código falso
                        codigoTela
                    );
                } catch (putError: any) {
                    console.warn('Erro ao atualizar código display (PUT pode não estar implementado ainda):', putError);
                    if (!putError.message?.includes('404') && !putError.message?.includes('Not Found')) {
                        throw putError;
                    }
                }
            }

            setSuccess(true);

            // Limpar campos após sucesso
            setTimeout(() => {
                setCodigo('');
                setSuccess(false);
                setShowCamera(false);
                setShowScreenList(false);
                setSelectedScreenId(null);
                setSelectedTelevisaoId(null);
                setScreenSearchTerm('');
                setTelevisaoSearchTerm('');
                setShowScreenDropdown(false);
                setShowTelevisaoDropdown(false);
                setInputMode('manual');
                setLinkType('tela');
                onClose();
            }, 3000);
        } catch (err) {
            setError(err instanceof Error ? err.message : `Erro ao vincular ${isTelevisao ? 'televisão' : 'tela'}`);
        } finally {
            setLoading(false);
        }
    }, [loading, onClose, televisoes]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        if (!codigo.trim()) {
            setError('Código do totem é obrigatório');
            return;
        }

        if (linkType === 'tela') {
            if (!selectedScreenId) {
                setError('Por favor, selecione uma tela');
                return;
            }
            await performLink(codigo.trim(), selectedScreenId, false);
        } else {
            if (!selectedTelevisaoId) {
                setError('Por favor, selecione uma televisão');
                return;
            }
            await performLink(codigo.trim(), selectedTelevisaoId, true);
        }
    }, [codigo, linkType, selectedScreenId, selectedTelevisaoId, performLink]);

    // Função auxiliar segura para parar o scanner
    const safeStopScanner = useCallback(async (scanner: Html5Qrcode | null) => {
        if (!scanner) {
            return;
        }

        try {
            // Sempre tentar parar primeiro, mesmo que o ref diga que não está rodando
            // Isso garante que qualquer stream de mídia seja liberado
            try {
                await scanner.stop();
            } catch (stopErr: any) {
                // Se o scanner não está rodando, isso é esperado - continuar
                if (!stopErr.message?.includes('not running') && !stopErr.message?.includes('not paused')) {
                    console.warn('Erro ao parar scanner:', stopErr);
                }
            }

            // Sempre limpar o scanner para remover qualquer referência
            try {
                scanner.clear();
            } catch (clearErr) {
                console.warn('Erro ao limpar scanner:', clearErr);
            }

            // Liberar qualquer stream de mídia que possa ter ficado ativo
            try {
                const elementId = 'qr-reader';
                const element = document.getElementById(elementId);
                if (element) {
                    // Procurar por elementos de vídeo dentro do container
                    const videoElements = element.querySelectorAll('video');
                    videoElements.forEach((video) => {
                        if (video.srcObject) {
                            const stream = video.srcObject as MediaStream;
                            stream.getTracks().forEach((track) => {
                                track.stop();
                            });
                            video.srcObject = null;
                        }
                    });

                    // Limpar o conteúdo do elemento para garantir que não há referências antigas
                    element.innerHTML = '';
                }
            } catch (cleanupErr) {
                console.warn('Erro ao limpar elemento DOM:', cleanupErr);
            }

            scannerRunningRef.current = false;
        } catch (err: any) {
            console.error('Erro geral ao parar scanner:', err);
            scannerRunningRef.current = false;
        }

        // Pequeno delay para garantir que tudo foi liberado
        await new Promise(resolve => setTimeout(resolve, 100));
    }, []);

    // Função para iniciar a câmera e ler QR code (tela)
    const startQRCodeScan = useCallback(async (screenId: number) => {
        // Limpar qualquer scanner anterior antes de iniciar uma nova sessão
        if (qrCodeScannerRef.current) {
            await safeStopScanner(qrCodeScannerRef.current);
            qrCodeScannerRef.current = null;
        }
        scannerRunningRef.current = false;
        setScannerRunning(false);
        setError(null);

        // Pequeno delay para garantir que tudo foi limpo
        await new Promise(resolve => setTimeout(resolve, 200));

        setSelectedScreenId(screenId);
        setSelectedTelevisaoId(null);
        setShowScreenList(false);
        setShowCamera(true);
    }, [safeStopScanner]);

    // Função para iniciar a câmera e ler QR code (televisão)
    const startQRCodeScanTelevisao = useCallback(async (televisaoId: number) => {
        // Limpar qualquer scanner anterior antes de iniciar uma nova sessão
        if (qrCodeScannerRef.current) {
            await safeStopScanner(qrCodeScannerRef.current);
            qrCodeScannerRef.current = null;
        }
        scannerRunningRef.current = false;
        setScannerRunning(false);
        setError(null);

        // Pequeno delay para garantir que tudo foi limpo
        await new Promise(resolve => setTimeout(resolve, 200));

        setSelectedTelevisaoId(televisaoId);
        setSelectedScreenId(null);
        setShowScreenList(false);
        setShowCamera(true);
    }, [safeStopScanner]);

    // useEffect para iniciar a câmera quando showCamera for true e o elemento estiver no DOM
    useEffect(() => {
        if (!showCamera || (!selectedScreenId && !selectedTelevisaoId)) {
            return;
        }

        let scanner: Html5Qrcode | null = null;
        let isMounted = true;

        const startCamera = async () => {
            try {
                setCameraLoading(true);

                // Garantir que qualquer scanner anterior foi completamente limpo
                if (qrCodeScannerRef.current) {
                    await safeStopScanner(qrCodeScannerRef.current);
                    qrCodeScannerRef.current = null;
                }
                scannerRunningRef.current = false;

                // Pequeno delay para garantir que tudo foi liberado
                await new Promise(resolve => setTimeout(resolve, 300));

                // Verificar se o elemento existe - tentar várias vezes se necessário
                const elementId = 'qr-reader';
                let element = document.getElementById(elementId);
                let attempts = 0;
                const maxAttempts = 10;

                while (!element && attempts < maxAttempts) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                    element = document.getElementById(elementId);
                    attempts++;
                }

                if (!element) {
                    console.error('Elemento qr-reader não encontrado após', maxAttempts, 'tentativas');
                    if (isMounted) {
                        setError('Erro ao inicializar leitor de QR code. Tente novamente.');
                        setShowCamera(false);
                        setCameraLoading(false);
                    }
                    return;
                }

                // Limpar o conteúdo do elemento antes de criar um novo scanner
                // Isso garante que não há referências antigas
                element.innerHTML = '';

                // Criar scanner
                scanner = new Html5Qrcode(elementId);
                qrCodeScannerRef.current = scanner;

                // Tentar iniciar a câmera
                try {
                    // Verificar se está em contexto seguro (HTTPS, localhost ou IP local)
                    // IPs locais (192.168.x.x, 10.x.x.x, 172.16-31.x.x) são considerados seguros para desenvolvimento
                    const hostname = window.location.hostname;
                    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
                    const isLocalIP = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/.test(hostname);
                    const isSecure = window.location.protocol === 'https:' || isLocalhost || isLocalIP;

                    if (!isSecure) {
                        // Avisar mas não bloquear - deixar o navegador decidir
                        console.warn('Acesso à câmera pode não funcionar sem HTTPS. Use localhost ou IP local para desenvolvimento.');
                    }

                    // Verificar permissões da câmera
                    try {
                        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                        // Se chegou aqui, tem permissão - parar o stream imediatamente
                        stream.getTracks().forEach(track => track.stop());
                    } catch (permError: any) {
                        if (permError.name === 'NotAllowedError' || permError.name === 'PermissionDeniedError') {
                            throw new Error('PERMISSION_DENIED');
                        } else if (permError.name === 'NotFoundError' || permError.name === 'DevicesNotFoundError') {
                            throw new Error('NO_CAMERA');
                        } else {
                            throw new Error('CAMERA_ERROR');
                        }
                    }

                    // Detectar se é mobile
                    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

                    // Tentar listar câmeras disponíveis
                    let cameraId: string | null = null;
                    try {
                        const devices = await navigator.mediaDevices.enumerateDevices();
                        const videoDevices = devices.filter(device => device.kind === 'videoinput');

                        // Priorizar câmera traseira (environment) no mobile
                        if (isMobile && videoDevices.length > 0) {
                            // Tentar encontrar câmera traseira pelo label
                            const backCamera = videoDevices.find(device =>
                                device.label.toLowerCase().includes('back') ||
                                device.label.toLowerCase().includes('rear') ||
                                device.label.toLowerCase().includes('environment')
                            );
                            cameraId = backCamera?.deviceId || videoDevices[videoDevices.length - 1].deviceId;
                        }
                    } catch (enumError) {
                        console.warn('Não foi possível listar câmeras:', enumError);
                        // Continuar sem cameraId específica
                    }

                    // Configurações diferentes para mobile e desktop
                    const config = isMobile
                        ? {
                            fps: 5, // Menor FPS no mobile para melhor performance
                            qrbox: { width: 250, height: 250 },
                            aspectRatio: 1.0,
                            disableFlip: false
                        }
                        : {
                            fps: 10,
                            qrbox: { width: 250, height: 250 },
                            aspectRatio: 1.0
                        };

                    // Configuração da câmera
                    const cameraConfig = cameraId
                        ? { deviceId: { exact: cameraId } }
                        : { facingMode: 'environment' };

                    await scanner.start(
                        cameraConfig,
                        config,
                        (decodedText) => {
                            // QR code lido com sucesso
                            if (isMounted && scanner && scannerRunningRef.current) {
                                scannerRunningRef.current = false;
                                safeStopScanner(scanner).then(() => {
                                    qrCodeScannerRef.current = null;
                                    setShowCamera(false);
                                    setCameraLoading(false);
                                    setScannerRunning(false);

                                    // Usar o código lido
                                    setCodigo(decodedText);

                                    // Fazer o link automaticamente
                                    if (selectedScreenId) {
                                        performLink(decodedText, selectedScreenId, false);
                                    } else if (selectedTelevisaoId) {
                                        performLink(decodedText, selectedTelevisaoId, true);
                                    }
                                }).catch((err) => {
                                    console.error('Erro ao parar scanner após leitura:', err);
                                    // Mesmo com erro ao parar, processar o código
                                    if (isMounted) {
                                        qrCodeScannerRef.current = null;
                                        setShowCamera(false);
                                        setCameraLoading(false);
                                        setScannerRunning(false);

                                        setCodigo(decodedText);
                                        if (selectedScreenId) {
                                            performLink(decodedText, selectedScreenId, false);
                                        } else if (selectedTelevisaoId) {
                                            performLink(decodedText, selectedTelevisaoId, true);
                                        }
                                    }
                                });
                            }
                        },
                        (errorMessage) => {
                            // Ignorar erros de leitura (continuar tentando)
                            // Não fazer nada, apenas continuar escaneando
                        }
                    );

                    // Câmera iniciada com sucesso
                    if (isMounted) {
                        scannerRunningRef.current = true;
                        setScannerRunning(true);
                        setCameraLoading(false);
                    }
                } catch (startError: any) {
                    console.error('Erro ao iniciar câmera:', startError);
                    if (isMounted) {
                        let errorMsg = 'Erro ao acessar a câmera.';

                        // Verificar tipo de erro
                        if (startError.message === 'PERMISSION_DENIED') {
                            errorMsg = 'Permissão da câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador ou do dispositivo.';
                        } else if (startError.message === 'NO_CAMERA') {
                            errorMsg = 'Nenhuma câmera encontrada. Verifique se há uma câmera disponível no dispositivo.';
                        } else if (startError.message === 'CAMERA_ERROR') {
                            errorMsg = 'Erro ao acessar a câmera. Verifique se o dispositivo possui câmera e se não está sendo usada por outro aplicativo.';
                        } else if (startError.message?.includes('Permission denied') ||
                            startError.message?.includes('NotAllowedError') ||
                            startError.name === 'NotAllowedError') {
                            errorMsg = 'Permissão da câmera negada. Por favor, permita o acesso à câmera nas configurações do navegador ou do dispositivo.';
                        } else if (startError.message?.includes('NotFoundError') ||
                            startError.message?.includes('No camera') ||
                            startError.name === 'NotFoundError') {
                            errorMsg = 'Nenhuma câmera encontrada. Verifique se há uma câmera disponível no dispositivo.';
                        } else if (startError.message?.includes('NotReadableError') ||
                            startError.name === 'NotReadableError') {
                            errorMsg = 'Câmera não pode ser acessada. Pode estar sendo usada por outro aplicativo. Feche outros aplicativos que possam estar usando a câmera.';
                        } else if (startError.message?.includes('OverconstrainedError') ||
                            startError.name === 'OverconstrainedError') {
                            errorMsg = 'Configuração da câmera não suportada. Tentando configuração alternativa...';
                            // Tentar com configuração mais simples
                            try {
                                await scanner.start(
                                    { facingMode: 'environment' },
                                    {
                                        fps: 5,
                                        qrbox: { width: 200, height: 200 }
                                    },
                                    (decodedText) => {
                                        if (isMounted && scanner && scannerRunningRef.current) {
                                            scannerRunningRef.current = false;
                                            safeStopScanner(scanner).then(() => {
                                                qrCodeScannerRef.current = null;
                                                setShowCamera(false);
                                                setCameraLoading(false);
                                                setScannerRunning(false);
                                                setCodigo(decodedText);
                                                if (selectedScreenId) {
                                                    performLink(decodedText, selectedScreenId, false);
                                                } else if (selectedTelevisaoId) {
                                                    performLink(decodedText, selectedTelevisaoId, true);
                                                }
                                            }).catch(() => {
                                                if (isMounted) {
                                                    qrCodeScannerRef.current = null;
                                                    setShowCamera(false);
                                                    setCameraLoading(false);
                                                    setScannerRunning(false);
                                                    setCodigo(decodedText);
                                                    if (selectedScreenId) {
                                                        performLink(decodedText, selectedScreenId, false);
                                                    } else if (selectedTelevisaoId) {
                                                        performLink(decodedText, selectedTelevisaoId, true);
                                                    }
                                                }
                                            });
                                        }
                                    },
                                    () => { }
                                );
                                scannerRunningRef.current = true;
                                setScannerRunning(true);
                                setCameraLoading(false);
                                return; // Sucesso com configuração alternativa
                            } catch (retryError) {
                                errorMsg = 'Não foi possível acessar a câmera. Verifique as permissões e tente novamente.';
                            }
                        }

                        setError(errorMsg);
                        setShowCamera(false);
                        setCameraLoading(false);
                    }
                }
            } catch (err: any) {
                console.error('Erro ao criar scanner:', err);
                if (isMounted) {
                    setError('Erro ao inicializar leitor de QR code. Tente novamente.');
                    setShowCamera(false);
                    setCameraLoading(false);
                }
            }
        };

        // Pequeno delay para garantir que o DOM está renderizado
        const timeoutId = setTimeout(() => {
            startCamera();
        }, 200);

        // Cleanup
        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            if (scanner) {
                safeStopScanner(scanner).catch(() => {
                    // Ignorar erros ao parar no cleanup
                });
            }
            scannerRunningRef.current = false;
            qrCodeScannerRef.current = null;
        };
    }, [showCamera, selectedScreenId, selectedTelevisaoId, performLink, safeStopScanner]);

    // Limpar scanner ao desmontar componente
    useEffect(() => {
        return () => {
            if (qrCodeScannerRef.current) {
                safeStopScanner(qrCodeScannerRef.current).catch(() => {
                    // Ignorar erros no cleanup
                });
                qrCodeScannerRef.current = null;
            }
            scannerRunningRef.current = false;
        };
    }, [safeStopScanner]);

    // Voltar para seleção de telas/televisões (mantém modo QR code)
    const goBackToSelection = useCallback(async () => {
        // Parar e limpar o scanner completamente
        if (qrCodeScannerRef.current) {
            await safeStopScanner(qrCodeScannerRef.current);
            qrCodeScannerRef.current = null;
        }

        // Limpar o elemento DOM
        const elementId = 'qr-reader';
        const element = document.getElementById(elementId);
        if (element) {
            element.innerHTML = '';
        }

        // Resetar estados da câmera mas manter o modo QR code e seleções
        scannerRunningRef.current = false;
        setScannerRunning(false);
        setShowCamera(false);
        setCameraLoading(false);
        setError(null);
        // Limpar seleções para voltar à lista
        setSelectedScreenId(null);
        setSelectedTelevisaoId(null);
        // Mostrar lista novamente e manter modo QR code
        setShowScreenList(true);
        setInputMode('qrcode');
    }, [safeStopScanner]);

    // Limpar scanner quando fechar a câmera
    const stopCamera = useCallback(async () => {
        // Parar e limpar o scanner completamente
        if (qrCodeScannerRef.current) {
            await safeStopScanner(qrCodeScannerRef.current);
            qrCodeScannerRef.current = null;
        }

        // Limpar o elemento DOM
        const elementId = 'qr-reader';
        const element = document.getElementById(elementId);
        if (element) {
            element.innerHTML = '';
        }

        // Resetar todos os estados
        scannerRunningRef.current = false;
        setScannerRunning(false);
        setShowCamera(false);
        setSelectedScreenId(null);
        setSelectedTelevisaoId(null);
        setCameraLoading(false);
        setError(null);
        setInputMode('manual'); // Remove o highlight do botão QR code
        setQrCodeSearchTerm(''); // Limpa o termo de pesquisa
    }, [safeStopScanner]);

    // Fechar dropdowns ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.dropdown-container')) {
                setShowScreenDropdown(false);
                setShowTelevisaoDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Se está mostrando a câmera
    if (showCamera && (selectedScreenId || selectedTelevisaoId)) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-md w-full p-6">
                    <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={goBackToSelection}
                                className="text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                                title="Voltar para seleção"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <h2 className="text-xl font-bold text-gray-800">
                                Escanear QR Code
                            </h2>
                        </div>
                        <button
                            onClick={stopCamera}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                            title="Fechar"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">
                            Posicione o QR code dentro da área de leitura
                        </p>
                        {error && (
                            <div className="mb-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-red-700 text-sm mb-2">{error}</p>
                                {error.includes('Permissão') || error.includes('HTTPS') || error.includes('câmera') ? (
                                    <button
                                        onClick={async () => {
                                            setError(null);
                                            // Limpar scanner antes de tentar novamente
                                            if (qrCodeScannerRef.current) {
                                                await safeStopScanner(qrCodeScannerRef.current);
                                                qrCodeScannerRef.current = null;
                                            }
                                            scannerRunningRef.current = false;
                                            setShowCamera(false);
                                            // Tentar novamente após um pequeno delay para garantir limpeza
                                            setTimeout(async () => {
                                                if (selectedScreenId) {
                                                    await startQRCodeScan(selectedScreenId);
                                                } else if (selectedTelevisaoId) {
                                                    await startQRCodeScanTelevisao(selectedTelevisaoId);
                                                }
                                            }, 500);
                                        }}
                                        className="text-sm text-red-700 underline hover:text-red-800"
                                    >
                                        Tentar novamente
                                    </button>
                                ) : null}
                            </div>
                        )}
                        {cameraLoading && (
                            <div className="mb-2 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center gap-2">
                                <Loader size={20} className="animate-spin text-blue-600" />
                                <p className="text-blue-700 text-sm">Iniciando câmera...</p>
                            </div>
                        )}
                        {!error && !cameraLoading && (
                            <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-yellow-700 text-xs">
                                    💡 Dica: Se a câmera não abrir, verifique as permissões do navegador nas configurações do dispositivo.
                                </p>
                            </div>
                        )}
                        <div
                            id="qr-reader"
                            ref={cameraContainerRef}
                            className="w-full rounded-lg overflow-hidden bg-black"
                            style={{ minHeight: '300px' }}
                        ></div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={goBackToSelection}
                            className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
                        >
                            <ChevronLeft size={18} />
                            Voltar
                        </button>
                        {error && (
                            <button
                                onClick={async () => {
                                    setError(null);
                                    // Limpar scanner antes de tentar novamente
                                    if (qrCodeScannerRef.current) {
                                        await safeStopScanner(qrCodeScannerRef.current);
                                        qrCodeScannerRef.current = null;
                                    }
                                    scannerRunningRef.current = false;
                                    setShowCamera(false);
                                    // Tentar novamente após um pequeno delay para garantir limpeza
                                    setTimeout(async () => {
                                        if (selectedScreenId) {
                                            await startQRCodeScan(selectedScreenId);
                                        } else if (selectedTelevisaoId) {
                                            await startQRCodeScanTelevisao(selectedTelevisaoId);
                                        }
                                    }, 500);
                                }}
                                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                            >
                                Tentar Novamente
                            </button>
                        )}
                        <button
                            onClick={stopCamera}
                            className={`px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 ${error ? 'flex-1' : 'flex-1'}`}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Se está mostrando a lista de telas/televisões (modo QR code)
    if (showScreenList && inputMode === 'qrcode') {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => {
                                    setShowScreenList(false);
                                    setInputMode('manual');
                                    setQrCodeSearchTerm('');
                                }}
                                className="text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
                                title="Voltar"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <h2 className="text-xl font-bold text-gray-800">
                                Selecione {linkType === 'tela' ? 'a Tela' : 'a Televisão'}
                            </h2>
                        </div>
                        <button
                            onClick={() => {
                                setShowScreenList(false);
                                setInputMode('manual');
                                setQrCodeSearchTerm('');
                            }}
                            className="text-gray-500 hover:text-gray-700 transition-colors"
                            title="Fechar"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Barra de Pesquisa */}
                    <div className="p-4 border-b">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                            <input
                                type="text"
                                value={qrCodeSearchTerm}
                                onChange={(e) => setQrCodeSearchTerm(e.target.value)}
                                placeholder={`Pesquisar ${linkType === 'tela' ? 'tela' : 'televisão'} por nome...`}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto p-4 flex-1">
                        {linkType === 'tela' ? (
                            filteredTelasQRCode.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">
                                    {qrCodeSearchTerm.trim() ? 'Nenhuma tela encontrada' : 'Nenhuma tela disponível'}
                                </p>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                    {filteredTelasQRCode.map((tela) => (
                                        <button
                                            key={tela.id}
                                            onClick={() => startQRCodeScan(tela.id!)}
                                            disabled={loading}
                                            className="p-2.5 border border-gray-200 rounded-md hover:border-blue-500 hover:bg-blue-50 transition-colors text-left disabled:opacity-50"
                                        >
                                            <div className="font-medium text-sm text-gray-800 truncate">{tela.nome}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">ID: {tela.id}</div>
                                            {tela.descricao && (
                                                <div className="text-xs text-gray-400 mt-0.5 line-clamp-1">{tela.descricao}</div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )
                        ) : (
                            filteredTelevisoesQRCode.length === 0 ? (
                                <p className="text-gray-500 text-center py-8">
                                    {qrCodeSearchTerm.trim() ? 'Nenhuma televisão encontrada' : 'Nenhuma televisão disponível'}
                                </p>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                    {filteredTelevisoesQRCode.map((tv) => (
                                        <button
                                            key={tv.cdTelevisao}
                                            onClick={() => tv.cdTelevisao != null && startQRCodeScanTelevisao(tv.cdTelevisao)}
                                            disabled={loading || tv.cdTelevisao == null}
                                            className="p-2.5 border border-gray-200 rounded-md hover:border-blue-500 hover:bg-blue-50 transition-colors text-left disabled:opacity-50"
                                        >
                                            <div className="font-medium text-sm text-gray-800 truncate">{tv.nmTelevisao}</div>
                                            <div className="text-xs text-gray-400 mt-0.5">
                                                ID: {tv.cdTelevisao}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // Modal principal
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">
                        Vincular ao Totem
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-700 text-sm">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-700 font-medium">Validação concluída!</p>
                    </div>
                )}

                {/* Seleção de tipo (Tela ou Televisão) */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        O que deseja vincular?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                setLinkType('tela');
                                setSelectedScreenId(null);
                                setSelectedTelevisaoId(null);
                                setScreenSearchTerm('');
                                setTelevisaoSearchTerm('');
                                setQrCodeSearchTerm('');
                                setShowScreenDropdown(false);
                                setShowTelevisaoDropdown(false);
                            }}
                            className={`p-4 border-2 rounded-lg transition-colors ${linkType === 'tela'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex flex-col items-center gap-2">
                                <Monitor size={24} />
                                <span className="text-sm font-medium">Tela</span>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setLinkType('televisao');
                                setSelectedScreenId(null);
                                setSelectedTelevisaoId(null);
                                setScreenSearchTerm('');
                                setTelevisaoSearchTerm('');
                                setQrCodeSearchTerm('');
                                setShowScreenDropdown(false);
                                setShowTelevisaoDropdown(false);
                            }}
                            className={`p-4 border-2 rounded-lg transition-colors ${linkType === 'televisao'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex flex-col items-center gap-2">
                                <List size={24} />
                                <span className="text-sm font-medium">Televisão</span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Seleção de modo */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        Como deseja vincular?
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                setInputMode('manual');
                                setShowScreenList(false);
                                setQrCodeSearchTerm('');
                            }}
                            className={`p-4 border-2 rounded-lg transition-colors ${inputMode === 'manual'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex flex-col items-center gap-2">
                                <span className="text-2xl">⌨️</span>
                                <span className="text-sm font-medium">Digitar Manualmente</span>
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setInputMode('qrcode');
                                setShowScreenList(true);
                                setQrCodeSearchTerm('');
                            }}
                            className={`p-4 border-2 rounded-lg transition-colors ${inputMode === 'qrcode'
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <div className="flex flex-col items-center gap-2">
                                <QrCode size={24} />
                                <span className="text-sm font-medium">Ler QR Code</span>
                            </div>
                        </button>
                    </div>
                </div>

                {/* Formulário manual */}
                {inputMode === 'manual' && (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Código do Totem *
                            </label>
                            <input
                                type="text"
                                value={codigo}
                                onChange={(e) => {
                                    setCodigo(e.target.value);
                                    setError(null);
                                }}
                                className="w-full p-3 border border-gray-300 rounded-lg"
                                placeholder="Digite o código do totem"
                                disabled={loading}
                                required
                            />
                        </div>

                        {linkType === 'tela' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Selecione a Tela *
                                </label>
                                <div className="relative dropdown-container">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={screenSearchTerm}
                                            onChange={(e) => {
                                                setScreenSearchTerm(e.target.value);
                                                setShowScreenDropdown(true);
                                                setError(null);
                                            }}
                                            onFocus={() => setShowScreenDropdown(true)}
                                            placeholder="Pesquisar ou selecionar tela..."
                                            className="w-full p-3 border border-gray-300 rounded-lg pr-10"
                                            disabled={loading}
                                        />
                                        <Search size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                    </div>

                                    {showScreenDropdown && filteredTelas.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                            {filteredTelas.map((tela) => (
                                                <button
                                                    key={tela.id}
                                                    type="button"
                                                    onClick={() => {
                                                        setSelectedScreenId(tela.id!);
                                                        setScreenSearchTerm(tela.nome);
                                                        setShowScreenDropdown(false);
                                                        setError(null);
                                                    }}
                                                    className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${selectedScreenId === tela.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                                        }`}
                                                >
                                                    <div className="font-medium text-gray-800">{tela.nome}</div>
                                                    {tela.descricao && (
                                                        <div className="text-sm text-gray-500 mt-1">{tela.descricao}</div>
                                                    )}
                                                    <div className="text-xs text-gray-400 mt-1">
                                                        Tela: {tela.id} • Tipo: {getTipoTelaNome(tela.tipo)}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {selectedScreenId && (
                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                        <div className="text-sm font-medium text-blue-800">
                                            Tela selecionada: {telas.find(t => t.id === selectedScreenId)?.nome}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Selecione a Televisão *
                                </label>
                                {loadingTelevisoes ? (
                                    <div className="p-4 text-center text-gray-500">
                                        <Loader size={18} className="animate-spin inline-block mr-2" />
                                        Carregando televisões...
                                    </div>
                                ) : (
                                    <div className="relative dropdown-container">
                                        <div className="relative">
                                            <input
                                                type="text"
                                                value={televisaoSearchTerm}
                                                onChange={(e) => {
                                                    setTelevisaoSearchTerm(e.target.value);
                                                    setShowTelevisaoDropdown(true);
                                                    setError(null);
                                                }}
                                                onFocus={() => setShowTelevisaoDropdown(true)}
                                                placeholder="Pesquisar ou selecionar televisão..."
                                                className="w-full p-3 border border-gray-300 rounded-lg pr-10"
                                                disabled={loading}
                                            />
                                            <Search size={20} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        </div>

                                        {showTelevisaoDropdown && filteredTelevisoes.length > 0 && (
                                            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                                {filteredTelevisoes.map((tv) => (
                                                    <button
                                                        key={tv.cdTelevisao}
                                                        type="button"
                                                        onClick={() => {
                                                            if (tv.cdTelevisao != null) {
                                                                setSelectedTelevisaoId(tv.cdTelevisao);
                                                                setTelevisaoSearchTerm(tv.nmTelevisao);
                                                                setShowTelevisaoDropdown(false);
                                                                setError(null);
                                                            }
                                                        }}
                                                        className={`w-full text-left px-4 py-3 hover:bg-gray-100 transition-colors ${selectedTelevisaoId === tv.cdTelevisao ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                                            }`}
                                                    >
                                                        <div className="font-medium text-gray-800">{tv.nmTelevisao}</div>
                                                        <div className="text-xs text-gray-400 mt-1">
                                                            ID: {tv.cdTelevisao}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {selectedTelevisaoId && (
                                    <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                        <div className="text-sm font-medium text-blue-800">
                                            Televisão selecionada: {televisoes.find(tv => tv.cdTelevisao === selectedTelevisaoId)?.nmTelevisao}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="flex gap-3 mt-6">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <>
                                        <Loader size={18} className="animate-spin" />
                                        Linkando...
                                    </>
                                ) : (
                                    'Vincular'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ScreensPageIntegrated;
